import React from 'react';
import {
  Modal,
  FormGroup,
  Row,
  FormControl,
  Checkbox,
  Col,
  ControlLabel,
  Button,
  ButtonToolbar,
} from 'react-bootstrap';

import { Field, reduxForm, formPropTypes, SubmissionError, formValueSelector } from 'redux-form';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import moment from 'moment';

import DepartmentInput from '../../../fields/DepartmentInput';
import DistrictInput from '../../../fields/DistrictInput';
import PayrollInput from '../../../fields/PayrollInput/index';
import UserInput from '../../../fields/UserInput';
import RoleInput from '../../../fields/RoleInput';

import { PhoneNumberField, DateField } from '../../../fields';
import { maybeId } from '../../../../utils';
import { CORPORATE_ADMIN } from '../../../authentication/reducer';

export const FORM_NAME = 'userForm';

const propTypes = {
  showUserEntryModal: PropTypes.bool.isRequired,
  closeUserModal: PropTypes.func.isRequired,
  dispatchCreateUpdateUser: PropTypes.func.isRequired,
  /* eslint-disable react/require-default-props */
  districtId: PropTypes.number,
  departmentId: PropTypes.number,
  /* eslint-enable react/require-default-props */
  /* eslint-disable react/forbid-prop-types */
  handleSubmit: PropTypes.func.isRequired,
  loggedInUser: PropTypes.object.isRequired,
  ...formPropTypes,
};

const validate = (values, props) => {
  const errors = {};

  if (!values.firstname) {
    errors.firstname = 'Required';
  }
  if (!values.lastname) {
    errors.lastname = 'Required';
  }
  if (values.cell_number && values.cell_number.length < 14) {
    errors.cell_number = 'Invalid Number. Must be 10 numbers of length.';
  }
  if (!values.default_department) {
    errors.default_department = 'Required';
  }
  if (!values.default_district) {
    errors.default_district = 'Required';
  }
  if (!values.payroll_type) {
    errors.payroll_type = 'Required';
  }
  if (!values.role) {
    errors.role = 'Required';
  }
  if (!values.accounting_system_id) {
    errors.accounting_system_id = 'Required';
  }
  if (values.payment_system_id && values.payment_system_id.length !== 10) {
    errors.payment_system_id = 'Invalid Payment System Id';
  }
  if (values.terminated_at && values.terminated_at.length < 10) {
    errors.terminated_at = 'Invalid Date';
  }
  if (!values.hired_at) {
    errors.hired_at = 'Required';
  }
  if (values.hired_at && values.hired_at.length < 10) {
    errors.hired_at = 'Invalid Date';
  }

  return errors;
};

/* eslint-disable */
const createRenderer = (render) => ({ input, meta, label, ...rest }) => {
  return (
    <div
      className={[
        meta.error && meta.touched ? 'form-error' : '',
        meta.active ? 'form-active' : '',
      ].join(' ')}
    >
      <ControlLabel>
        {label}
      </ControlLabel>

      {render(input, label, rest)}

      {meta.error && meta.touched &&
      <span>
        {meta.error}
      </span>
      }
    </div>
  );
};
/* eslint-enable */

const RenderInput = createRenderer((input, label, { disabled }) => {
  return (
    <FormControl type="input" {...input} disabled={disabled} />
  );
});

const RenderCheckBox = createRenderer((input, label, { disabled }) => {
  return (
    <Checkbox checked={input.value} disabled={disabled} {...input} />
  );
});

const RenderDepartment = createRenderer((input, label, { disabled, districtId }) => {
  return (
    <DepartmentInput districtId={districtId} disabled={disabled} {...input} />
  );
});

const RenderDistrict = createRenderer((input, label, { disabled, departmentId }) => {
  return (
    <DistrictInput departmentId={departmentId} disabled={disabled} {...input} />
  );
});

const RenderPayroll = createRenderer((input, label, { disabled }) => {
  return (
    <PayrollInput disabled={disabled} {...input} />
  );
});

const RenderAllUsers = createRenderer((input, label, { disabled }) => {
  return (
    <UserInput disabled={disabled} {...input} />
  );
});


const RenderRoleInput = createRenderer((input, label, { disabled }) => {
  return (
    <RoleInput disabled={disabled} {...input} />
  );
});

const UserModal = (props) => {
  const {
    showUserEntryModal,
    closeUserModal,
    handleSubmit,
    dispatchCreateUpdateUser,
    initialValues,
    submitting,
    pristine,
    invalid,
    error,
    districtId,
    departmentId,
    loggedInUser,
  } = props;

  const isNewRecord = !(initialValues.id);

  const onSubmit = async (values) => {
    /* eslint-disable camelcase */
    const {
      role,
      cell_number, email, firstname, lastname, accounting_system_id, payroll_type,
      default_district,
      default_department,
      enabled,
      scanner,
      terminated_at: terminatedAt,
      hired_at: hiredAt,
      payment_system_id,
    } = values;
    /* eslint-ensable camelcase */

    const user = {
      default_district_id: default_district.id,
      default_department_id: default_department.id,
      role,
      cell_number,
      email,
      firstname,
      lastname,
      accounting_system_id,
      payroll_type,
      enabled,
      hired_at: hiredAt,
      terminated_at: terminatedAt,
      payment_system_id,
      scanner,
    };


    if (values.id) {
      user.id = values.id;
      if (hiredAt) {
        user.hired_at = moment(hiredAt, 'MM/DD/YYYY').toISOString();
      }
      if (terminatedAt) {
        user.terminated_at = moment(terminatedAt, 'MM/DD/YYYY').toISOString();
      }
    }
    if (values.supervisor) {
      user.supervisor_id = values.supervisor.id;
    }

    /* Add try catch block here to handle errors */
    try {
      await dispatchCreateUpdateUser(user);
    } catch (e) {
      const message = e.graphQLErrors.reduce((errorMessage, gqlError) => {
        return errorMessage + gqlError.details;
      }, '');
      throw new SubmissionError({ _error: message });
    }
  };

  const isDisabled = (loggedInUser.role !== CORPORATE_ADMIN);
  return (
    <div>
      <Modal
        show={showUserEntryModal}
        onHide={closeUserModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormGroup>
              <Row>
                <Col xs={6}>
                  <FormGroup>
                    <Field
                      name="firstname"
                      type="text"
                      label="First Name"
                      component={RenderInput}
                    />
                  </FormGroup>
                </Col>
                <Col xs={6}>
                  <FormGroup>
                    <Field name="lastname" type="text" label="Last Name" component={RenderInput} />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={6}>
                  <FormGroup>
                    <Field
                      name="email"
                      type="text"
                      label="Email"
                      component={RenderInput}
                    />
                  </FormGroup>
                </Col>
                <Col xs={6}>
                  <Field
                    name="scanner"
                    label="Scanner"
                    disabled={isDisabled}
                    component={RenderCheckBox}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={6}>
                  <PhoneNumberField name="cell_number" label="Cell Number" />
                </Col>
                <Col xs={6}>
                  <FormGroup>
                    <Field
                      name="enabled"
                      label="Enable Login"
                      disabled={isDisabled}
                      component={RenderCheckBox}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={6}>
                  <FormGroup>
                    <Field
                      districtId={districtId}
                      name="default_department"
                      type="select"
                      label="Department"
                      format={null}
                      disabled={isDisabled}
                      component={RenderDepartment}
                    />
                  </FormGroup>
                </Col>
                <Col xs={6}>
                  <FormGroup>
                    <Field
                      departmentId={departmentId}
                      name="default_district"
                      type="select"
                      label="District"
                      format={null}
                      disabled={isDisabled}
                      component={RenderDistrict}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col xs={6}>
                  <FormGroup>
                    <Field
                      name="payroll_type"
                      type="select"
                      label="Payroll"
                      format={null}
                      disabled={isDisabled}
                      component={RenderPayroll}
                    />
                  </FormGroup>
                </Col>
                <Col xs={6}>
                  <FormGroup>
                    <Field
                      name="supervisor"
                      type="select"
                      label="Supervisor"
                      format={null}
                      disabled={isDisabled}
                      component={RenderAllUsers}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={6}>
                  <FormGroup>
                    <Field
                      name="accounting_system_id"
                      type="text"
                      label="Accounting Id"
                      disabled={isDisabled}
                      component={RenderInput}
                    />
                  </FormGroup>
                </Col>

                <Col xs={6}>
                  <FormGroup>
                    <Field
                      name="role"
                      type="select"
                      label="Role"
                      disabled={isDisabled}
                      component={RenderRoleInput}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={6}>
                  <FormGroup>
                    <Field
                      name="payment_system_id"
                      type="text"
                      label="Payment System Id"
                      disabled={isDisabled}
                      component={RenderInput}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <DateField
                    name="hired_at"
                    label="Hire Date"
                    componentProps={{ disabled: (isDisabled) ? 'disabled' : '' }}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <DateField
                    name="terminated_at"
                    label="Termination Date"
                    componentProps={{ disabled: (isDisabled) ? 'disabled' : '' }}
                  />
                </Col>
              </Row>
            </FormGroup>
            <Row>
              <Col md={12}>
                <ButtonToolbar className="pull-right">
                  <Button bsStyle="warning" onClick={closeUserModal}>
                    Cancel
                  </Button>
                  <Button
                    bsStyle="primary"
                    type="submit"
                    disabled={submitting || pristine || invalid}
                  >
                    {isNewRecord && 'Create'}
                    {!isNewRecord && 'Update'}
                  </Button>
                </ButtonToolbar>
              </Col>
            </Row>
            <label>{error && <strong>{error}</strong>}</label>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

UserModal.propTypes = propTypes;

const withForm = reduxForm({
  form: FORM_NAME,
  enableReinitialize: true,
  validate,
  initialValues: {
    enabled: false,
    scanner: false,
  },
});

const selector = formValueSelector(FORM_NAME);

const withRedux = connect((state) => {
  const district = selector(state, 'default_district');
  const department = selector(state, 'default_department');
  const districtId = maybeId(district);
  const departmentId = maybeId(department);

  return {
    districtId,
    departmentId,
  };
});

const enhance = compose(
  withForm,
  withRedux,
);

export default enhance(UserModal);
